import { useState, useEffect } from "react";
import { useParams, Link, Navigate, useLocation } from "react-router-dom";
import { ArrowLeft, CheckCircle, Clock, Timer, X } from "lucide-react";
import NavBar from "../components/NavBar";
import { toast } from "sonner";
import MediaDisplay from "../components/MediaDisplay";
import MediaViewer from "../components/MediaViewer";
import DownloadableFileAttachment from "../components/DownloadableFileAttachment";
import ChatButton from '../components/ChatButton';
import '../styles/media.css';

interface Media {
  id: string;
  url: string;
  name: string;
  type: string;
  mimeType: string;
  size?: number;
}

interface Offer {
  id: string;
  willId: string;
  willTitle: string;
  description: string;
  status: string;
  price: number;
  currency: string;
  estimatedCompletionDate: string;
  createdAt: string;
  updatedAt: string;
  media?: Media[];
  buyer: {
    id: string;
    username: string;
    profilePicture?: string;
  };
}

interface LocationState {
  offer: Offer;
}

const getProgressValue = (status: string): number => {
  switch (status) {
    case 'PENDING': return 25;
    case 'IN_REVIEW': return 50;
    case 'IN_PROGRESS': return 75;
    case 'COMPLETED': return 100;
    case 'PAID': return 100;
    default: return 0;
  }
};

const getCurrencySymbol = (currency: string): string => {
  const currencySymbols: { [key: string]: string } = {
    USD: '$',
    EUR: '€',
    ILS: '₪',
    GBP: '£'
  };
  return currencySymbols[currency] || '₪';
};

const ProviderOfferDetails = () => {
  const { id } = useParams();
  const location = useLocation();
  const state = location.state as LocationState;
  const [offer, setOffer] = useState<Offer | null>(state?.offer || null);
  const [loading, setLoading] = useState(!state?.offer);
  const [showMediaViewer, setShowMediaViewer] = useState(false);
  const [mediaViewerIndex, setMediaViewerIndex] = useState(0);
  const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:8080';

  useEffect(() => {
    if (!state?.offer) {
      loadOffer();
    }
  }, [id]);

  const loadOffer = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/offers/${id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to load offer');
      
      const data = await response.json();
      setOffer(data);
    } catch (error) {
      console.error('Failed to load offer:', error);
      toast.error("Failed to load offer details");
    } finally {
      setLoading(false);
    }
  };

  const statusColors = {
    PENDING: "bg-yellow-100 text-yellow-800 border-yellow-300",
    IN_REVIEW: "bg-orange-100 text-orange-800 border-orange-300",
    IN_PROGRESS: "bg-blue-100 text-blue-800 border-blue-300",
    COMPLETED: "bg-green-100 text-green-800 border-green-300",
    REJECTED: "bg-red-100 text-red-800 border-red-300",
    CANCELLED: "bg-gray-100 text-gray-800 border-gray-300"
  };

  const statusIcons = {
    PENDING: <Clock className="h-4 w-4" />,
    IN_REVIEW: <Timer className="h-4 w-4" />,
    IN_PROGRESS: <Timer className="h-4 w-4" />,
    COMPLETED: <CheckCircle className="h-4 w-4" />,
    REJECTED: <X className="h-4 w-4" />,
    CANCELLED: <X className="h-4 w-4" />
  };

  // Process media to ensure URLs are complete
  const processedMedia = offer?.media?.map(item => {
    // Determine the media type based on mimeType or existing type
    let type = item.type;
    
    // If no type is set but mimeType exists, determine type from mimeType
    if (!type && item.mimeType) {
      if (item.mimeType.startsWith('image/')) {
        type = 'image';
      } else if (item.mimeType.startsWith('video/')) {
        type = 'video';
      } else {
        type = 'document';
      }
    } else if (!type) {
      // Default to document if no type info is available
      type = 'document';
    }
    
    return {
      ...item,
      url: item.url.startsWith('http') ? item.url : `${baseUrl}${item.url}`,
      id: item.id || `media-${Math.random()}`,
      type,
      name: item.name || 'File',
      mimeType: item.mimeType || 'application/octet-stream'
    };
  }) || [];

  // Improved filtering for visual media and documents
  const visualMedia = processedMedia.filter(item => 
    item.type === 'image' || 
    item.type === 'video' || 
    (item.mimeType && (item.mimeType.startsWith('image/') || item.mimeType.startsWith('video/')))
  );

  const documentMedia = processedMedia.filter(item => 
    (item.type === 'document' || 
    (item.type !== 'image' && item.type !== 'video')) && 
    (!item.mimeType || 
     (!item.mimeType.startsWith('image/') && !item.mimeType.startsWith('video/')))
  );

  if (!localStorage.getItem('accessToken')) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <NavBar />
      <main className="container mx-auto px-4 pt-24 pb-12">
        <div className="max-w-3xl mx-auto">
          <Link 
            to="/my-offers"
            className="inline-flex items-center text-gray-600 hover:text-gray-900 text-sm mb-6"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to My Offers
          </Link>

          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading offer details...</p>
            </div>
          ) : offer ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-semibold text-gray-900">{offer.willTitle}</h1>
                <span className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1.5 border ${statusColors[offer.status as keyof typeof statusColors]}`}>
                  {statusIcons[offer.status as keyof typeof statusIcons]}
                  {offer.status}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-6 mb-8">
                <div className="space-y-2">
                  <div className="flex items-baseline gap-2">
                    <span className="text-sm font-bold text-gray-600 uppercase">
                      Reward:
                    </span>
                    <span className="text-2xl font-bold text-green-900 bg-green-50 px-3 py-1 rounded-md">
                      {getCurrencySymbol(offer.currency)}{offer.price}
                    </span>
                  </div>
                </div>
                {offer.estimatedCompletionDate && (
                  <div className="space-y-2">
                    <p className="text-sm text-gray-500">Estimated Completion</p>
                    <p className="font-medium">
                      {new Date(offer.estimatedCompletionDate).toLocaleDateString()}
                    </p>
                  </div>
                )}
              </div>

              <div className="mb-8">
                <h2 className="text-lg font-medium mb-2">Your Offer Description</h2>
                <p className="text-gray-600">{offer.description}</p>
              </div>

              {/* Visual Media Section */}
              {visualMedia.length > 0 && (
                <div className="mb-8">
                  <h2 className="text-lg font-medium mb-4">Media</h2>
                  <div className="media-carousel-container">
                    <MediaDisplay 
                      media={visualMedia}
                      onClick={(index) => {
                        setMediaViewerIndex(index);
                        setShowMediaViewer(true);
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Document Attachments Section */}
              {documentMedia.length > 0 && (
                <div className="document-attachments mb-8">
                  <div className="flex items-center mb-3">
                    <h3 className="text-sm font-medium text-gray-700">Attachments</h3>
                    <span className="ml-2 px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full">
                      {documentMedia.length}
                    </span>
                  </div>
                  <div className="space-y-2 bg-gray-50 p-4 rounded-lg border border-gray-100">
                    {documentMedia.map((doc) => (
                      <DownloadableFileAttachment 
                        key={doc.id || `doc-${Math.random()}`} 
                        file={doc} 
                      />
                    ))}
                  </div>
                </div>
              )}

              <div className="space-y-3 mb-8">
                <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                  <div 
                    className="h-full rounded-full transition-all duration-700 ease-in-out bg-gradient-to-r from-yellow-400 via-blue-500 to-green-500"
                    style={{ width: `${getProgressValue(offer.status)}%` }}
                  />
                </div>
                <div className="flex justify-between text-sm text-gray-500">
                  <span className="flex items-center gap-1">
                    📝 Offer Sent
                  </span>
                  <span className="flex items-center gap-1">
                    👀 In Review
                  </span>
                  <span className="flex items-center gap-1">
                    🔨 In Progress
                  </span>
                  <span className="flex items-center gap-1">
                    ✨ Completed
                  </span>
                </div>
              </div>
              
              <div className="text-sm text-gray-500 mt-4">
                <p>Created: {new Date(offer.createdAt).toLocaleString()}</p>
                {offer.updatedAt && offer.updatedAt !== offer.createdAt && (
                  <p>Last updated: {new Date(offer.updatedAt).toLocaleString()}</p>
                )}
              </div>

              {/* Buyer info with chat button */}
              <div className="bg-white rounded-lg shadow p-6 mt-6">
                <h2 className="text-lg font-semibold mb-4">Buyer Information</h2>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center mr-3">
                      {offer.buyer.profilePicture ? (
                        <img 
                          src={offer.buyer.profilePicture} 
                          alt={offer.buyer.username} 
                          className="w-10 h-10 rounded-full"
                        />
                      ) : (
                        <span className="text-gray-500 font-medium">
                          {offer.buyer.username.charAt(0).toUpperCase()}
                        </span>
                      )}
                    </div>
                    <div>
                      <p className="font-medium">{offer.buyer.username}</p>
                      <p className="text-sm text-gray-500">Buyer</p>
                    </div>
                  </div>
                  
                  {/* Chat button */}
                  <ChatButton 
                    userId={offer.buyer.id} 
                    buttonText="Contact Buyer"
                    className="bg-green-500 hover:bg-green-600"
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-600">Offer not found</p>
            </div>
          )}
        </div>
      </main>

      {/* Full-screen Media Viewer */}
      {showMediaViewer && (
        <MediaViewer
          media={visualMedia}
          initialIndex={mediaViewerIndex}
          onClose={() => setShowMediaViewer(false)}
        />
      )}
    </div>
  );
};

export default ProviderOfferDetails; 